/**
 * Static UK network coverage info for O2, Three, EE and Vodafone.
 * Source: https://5g.co.uk/coverage/ (fetched 2026-05-28).
 *
 * Used by the public Network Coverage page expandable info blocks.
 */

export type NetworkKey = 'three' | 'ee' | 'o2' | 'vodafone'

export interface NetworkInfo {
  key: NetworkKey
  name: string
  pop5g: string
  pop4g: string
  speed5g: string
  speed4g: string
  bands4g: string[]
  bands5g: string[]
  cities5g: string[]
  statusUrl: string
}

export const NETWORK_INFO: NetworkInfo[] = [
  {
    key: 'three',
    name: 'Three',
    pop5g: 'Over 62% UK outdoor population coverage',
    pop4g: '99.8% population coverage and 4G coverage >99%',
    speed5g: '208.9 Mbps (Opensignal)',
    speed4g: '22.2 Mbps (Opensignal)',
    bands4g: ['800 MHz', '1.4 GHz', '1.8 GHz', '2.1 GHz'],
    bands5g: ['700 MHz', '3.4 GHz', '3.6–4 GHz', '26 GHz', '40 GHz'],
    cities5g: [
      'Aberdeen', 'Bath', 'Birmingham', 'Blackburn', 'Blackpool', 'Bolton', 'Bournemouth',
      'Bradford', 'Brighton', 'Bristol', 'Bromley', 'Cambridge', 'Canterbury', 'Cardiff',
      'Carlisle', 'Central London', 'Chelmsford', 'Chester', 'Cleveland', 'Colchester',
      'Coventry', 'Crewe', 'Croydon', 'Darlington', 'Dartford', 'Derby', 'Doncaster',
      'Dudley', 'Dumfries and Galloway', 'Dundee', 'Durham', 'East London', 'Edinburgh',
      'Enfield', 'Falkirk and Stirling', 'Glasgow', 'Gloucester', 'Guildford', 'Halifax',
      'Harrogate', 'Harrow', 'Hemel Hempstead', 'Hereford', 'Huddersfield', 'Hull',
      'Ilford', 'Inverness', 'Ipswich', 'Kingston upon Thames', 'Kirkcaldy', 'Lancaster',
      'Leeds', 'Leicester', 'Lincoln', 'Liverpool', 'Llandudno', 'Luton', 'Manchester',
      'Milton Keynes', 'Newcastle upon Tyne', 'Newport', 'North London', 'North West London',
      'Northampton', 'Northern Ireland', 'Norwich', 'Nottingham', 'Oldham', 'Oxford',
      'Paisley', 'Perth', 'Peterborough', 'Plymouth', 'Portsmouth', 'Preston', 'Reading',
      'Redhill', 'Rochester', 'Romford', 'Sheffield', 'Slough', 'South East London',
      'South West London', 'Southall', 'Southampton', 'Southend-on-Sea', 'St Albans',
      'Stevenage', 'Stockport', 'Stoke-on-Trent', 'Sunderland', 'Sutton', 'Swansea',
      'Swindon', 'Twickenham', 'Wakefield', 'Walsall', 'Warrington', 'Watford',
      'West London', 'Wigan', 'Wolverhampton', 'York',
    ],
    statusUrl: 'https://www.three.co.uk/support/network-status',
  },
  {
    key: 'ee',
    name: 'EE',
    pop5g: 'Over 87% UK population coverage',
    pop4g: '>99% UK population coverage',
    speed5g: '96.8 Mbps (Opensignal)',
    speed4g: '36.4 Mbps (Opensignal)',
    bands4g: ['800 MHz', '1.8 GHz', '2.1 GHz', '2.6 GHz'],
    bands5g: ['700 MHz', '3.4 GHz', '3.6 GHz', '26 GHz', '40 GHz'],
    cities5g: [
      'Aberdeen', 'Bath', 'Birmingham', 'Blackburn', 'Blackpool', 'Bolton', 'Bournemouth',
      'Bradford', 'Brighton', 'Bristol', 'Bromley', 'Cambridge', 'Canterbury', 'Cardiff',
      'Carlisle', 'Central London', 'Chelmsford', 'Chester', 'Cleveland', 'Colchester',
      'Coventry', 'Crewe', 'Croydon', 'Darlington', 'Dartford', 'Derby', 'Doncaster',
      'Dorchester', 'Dudley', 'Dumfries and Galloway', 'Dundee', 'Durham', 'East London',
      'Edinburgh', 'Enfield', 'Exeter', 'Falkirk and Stirling', 'Galashiels', 'Glasgow',
      'Gloucester', 'Guildford', 'Halifax', 'Harrogate', 'Harrow', 'Hemel Hempstead',
      'Hereford', 'Huddersfield', 'Hull', 'Ilford', 'Inverness', 'Ipswich', 'Kilmarnock',
      'Kingston upon Thames', 'Kirkcaldy', 'Lancaster', 'Leeds', 'Leicester', 'Lincoln',
      'Liverpool', 'Llandudno', 'Luton', 'Manchester', 'Milton Keynes', 'Motherwell',
      'Newcastle upon Tyne', 'Newport', 'North London', 'North West London', 'Northampton',
      'Northern Ireland', 'Norwich', 'Nottingham', 'Oldham', 'Outer Hebrides', 'Oxford',
      'Paisley', 'Perth', 'Peterborough', 'Plymouth', 'Portsmouth', 'Preston', 'Reading',
      'Redhill', 'Rochester', 'Romford', 'Salisbury', 'Sheffield', 'Shrewsbury', 'Slough',
      'South East London', 'South West London', 'Southall', 'Southampton', 'Southend-on-Sea',
      'St Albans', 'Stevenage', 'Stockport', 'Stoke-on-Trent', 'Sunderland', 'Sutton',
      'Swansea', 'Swindon', 'Taunton', 'Telford', 'Tonbridge', 'Torquay', 'Truro',
      'Twickenham', 'Wakefield', 'Walsall', 'Warrington', 'Watford', 'West London',
      'Wigan', 'Wolverhampton', 'Worcester', 'York',
    ],
    statusUrl: 'https://ee.co.uk/help/help-new/safety-and-security-and-startstop/network/check-service-status',
  },
  {
    key: 'o2',
    name: 'O2',
    pop5g: 'Over 77% UK population coverage',
    pop4g: '>99% UK population coverage',
    speed5g: '80.1 Mbps (Opensignal)',
    speed4g: '18.2 Mbps (Opensignal)',
    bands4g: ['700 MHz', '800 MHz', '900 MHz', '1.4 GHz', '1.8 GHz', '2.1 GHz', '2.3 GHz', '2.6 GHz'],
    bands5g: ['700 MHz', '3.4 GHz', '3.6 GHz', '26 GHz', '40 GHz'],
    cities5g: [
      'Aberdeen', 'Birmingham', 'Blackburn', 'Blackpool', 'Bolton', 'Bournemouth', 'Bradford',
      'Brighton', 'Bristol', 'Bromley', 'Cambridge', 'Canterbury', 'Cardiff', 'Central London',
      'Chelmsford', 'Chester', 'Cleveland', 'Colchester', 'Coventry', 'Croydon', 'Darlington',
      'Dartford', 'Derby', 'Doncaster', 'Dudley', 'Dundee', 'Durham', 'East London',
      'Edinburgh', 'Enfield', 'Falkirk and Stirling', 'Galashiels', 'Glasgow', 'Gloucester',
      'Guildford', 'Halifax', 'Harrogate', 'Harrow', 'Hemel Hempstead', 'Huddersfield',
      'Hull', 'Ilford', 'Inverness', 'Ipswich', 'Kingston upon Thames', 'Kirkcaldy',
      'Kirkwall', 'Lancaster', 'Leeds', 'Leicester', 'Lincoln', 'Liverpool', 'Llandudno',
      'Luton', 'Manchester', 'Milton Keynes', 'Newcastle upon Tyne', 'Newport',
      'North London', 'North West London', 'Northampton', 'Northern Ireland', 'Norwich',
      'Nottingham', 'Oldham', 'Outer Hebrides', 'Oxford', 'Paisley', 'Perth', 'Peterborough',
      'Plymouth', 'Portsmouth', 'Preston', 'Reading', 'Redhill', 'Rochester', 'Romford',
      'Sheffield', 'Slough', 'South East London', 'South West London', 'Southall',
      'Southampton', 'Southend-on-Sea', 'St Albans', 'Stevenage', 'Stockport',
      'Stoke-on-Trent', 'Sunderland', 'Sutton', 'Swansea', 'Swindon', 'Twickenham',
      'Wakefield', 'Walsall', 'Warrington', 'Watford', 'West London', 'Wigan',
      'Wolverhampton', 'York',
    ],
    statusUrl: 'https://status.o2.co.uk/',
  },
  {
    key: 'vodafone',
    name: 'Vodafone',
    pop5g: 'Hundreds of UK towns & cities',
    pop4g: '>99% UK population coverage',
    speed5g: '138.7 Mbps (Opensignal)',
    speed4g: '22.4 Mbps (Opensignal)',
    bands4g: ['800 MHz', '900 MHz', '1.4 GHz', '1.8 GHz', '2.1 GHz', '2.6 GHz'],
    bands5g: ['3.4 GHz', '3.6 GHz', '26 GHz', '40 GHz'],
    cities5g: [
      'Aberdeen', 'Birmingham', 'Blackburn', 'Blackpool', 'Bolton', 'Bournemouth', 'Bradford',
      'Brighton', 'Bristol', 'Bromley', 'Cambridge', 'Canterbury', 'Cardiff', 'Carlisle',
      'Central London', 'Chelmsford', 'Cleveland', 'Colchester', 'Coventry', 'Crewe',
      'Croydon', 'Darlington', 'Dartford', 'Derby', 'Doncaster', 'Dudley',
      'Dumfries and Galloway', 'Dundee', 'Durham', 'East London', 'Edinburgh', 'Enfield',
      'Exeter', 'Falkirk and Stirling', 'Glasgow', 'Gloucester', 'Guildford', 'Halifax',
      'Harrow', 'Hemel Hempstead', 'Huddersfield', 'Hull', 'Ilford', 'Inverness', 'Ipswich',
      'Kingston upon Thames', 'Kirkcaldy', 'Lancaster', 'Leeds', 'Leicester', 'Lincoln',
      'Liverpool', 'Llandudno', 'Luton', 'Manchester', 'Milton Keynes',
      'Newcastle upon Tyne', 'Newport', 'North London', 'North West London', 'Northampton',
      'Northern Ireland', 'Norwich', 'Nottingham', 'Oldham', 'Oxford', 'Paisley',
      'Peterborough', 'Plymouth', 'Portsmouth', 'Preston', 'Reading', 'Redhill', 'Rochester',
      'Romford', 'Sheffield', 'Slough', 'South East London', 'South West London', 'Southall',
      'Southampton', 'Southend-on-Sea', 'St Albans', 'Stevenage', 'Stockport',
      'Stoke-on-Trent', 'Sunderland', 'Sutton', 'Swansea', 'Swindon', 'Taunton',
      'Twickenham', 'Wakefield', 'Walsall', 'Warrington', 'Watford', 'West London',
      'Wigan', 'Wolverhampton', 'York',
    ],
    statusUrl: 'https://www.vodafone.co.uk/network/status-checker',
  },
]
